import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useModalContext } from '@bcraft-frontend-lib/core'
import block from 'bem-cn'

import { MODAL_ROUTE_PATH, MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_URLS } from 'router/routes'

import { ReactComponent as XSVG } from 'shared/img/x.svg'

import { BonusPromotionStatus, IUserPromotion } from 'shared/models/Promotion'
import { getRouteParamsByStrings } from 'shared/utils/getRouteParamsByStrings'

import { changeActiveUserPromotion } from 'features/bonusSystem/actions'
import { selectBonusPromotions } from 'features/bonusSystem/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { ModalWrapper } from 'components/ModalWrapper'
import Button from 'components/UIKit/Button/Button'

import './ConfirmActivateBonus.scss'

const b = block('confirm-activate-bonus')

interface IConfirmActivateProps {
  path: string
}

interface ParamsMatch {
  bonusId: string
}

export const ConfirmActivateBonus: React.FC<IConfirmActivateProps> = ({ path }) => {
  const dispatch = useDispatch()
  const params = getRouteParamsByStrings<ParamsMatch>(MODAL_ROUTE_PATH.CONFIRM_ACTIVATE_BONUS, path)
  const bonusPromotions = useSelector(selectBonusPromotions, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).bonuses

  const history = useHistory()

  const { closeModal } = useModalContext()

  const [bonusId, setBonusId] = useState(params?.bonusId || '')

  const findBonusId = useMemo(
    () => bonusPromotions.find((bonus: IUserPromotion) => bonus.id === +(+bonusId))?.id || '',
    [bonusId, bonusPromotions],
  )

  const callback = useCallback(() => {
    closeModal(MODAL_ROUTE_URLS.CONFIRM_ACTIVATE_BONUS(bonusId))()
    history.push(ROUTE_URLS.PROFILE_PROMOTIONS_STATUS(BonusPromotionStatus.active))
  }, [bonusId, closeModal, history])

  const onClose = () => {
    closeModal(path)()
  }

  const onConfirmActivateHandler = useCallback(() => {
    dispatch(
      changeActiveUserPromotion({
        id: bonusId,
        callback,
      }),
    )
  }, [bonusId, callback, dispatch])

  useEffect(() => {
    if (String(findBonusId)) {
      setBonusId(String(findBonusId))
    } else {
      closeModal(MODAL_ROUTE_URLS.CONFIRM_ACTIVATE_BONUS(bonusId))()
    }
  }, [findBonusId])

  return (
    <ModalWrapper title={locale.confirmActivateBonus} className={b()} onClose={onClose}>
      <div className={b('text')}>{locale.changeActiveBonusText}</div>
      <div className={b('button-wrapper')}>
        <div className={b('button')}>
          <Button text={locale.cancel} onClick={closeModal(MODAL_ROUTE_URLS.CONFIRM_ACTIVATE_BONUS(bonusId))} />
        </div>
        <div className={b('button')}>
          <Button text={locale.activate} onClick={onConfirmActivateHandler} />
        </div>
      </div>
    </ModalWrapper>
  )
}
